import { useMemo, useReducer } from "react";
import { createEmptyTagGrid, tagGridReducer } from "../helpers/TagGridReducer";

/**
 * The tag grid's rows, saved baselines, dirty rows and selection, with one function per kind of
 * change. The functions keep their identity across renders.
 */
export const useTagGrid = () => {
  const [grid, dispatch] = useReducer(
    tagGridReducer,
    undefined,
    createEmptyTagGrid
  );

  const actions = useMemo(
    () => ({
      /** Shows freshly loaded tags, with nothing dirty or selected. */
      replaceGrid: (tags, editedTags) =>
        dispatch({ type: "replaceGrid", tags, editedTags }),
      restoreSession: (session) =>
        dispatch({ type: "restoreSession", session }),
      /** Appends tags, resolving Tag ID collisions by `collisionMode`. */
      appendTags: (tags, collisionMode) =>
        dispatch({ type: "appendTags", tags, collisionMode }),
      insertEmpty: (at) => dispatch({ type: "insertEmpty", at }),
      /** Inserts an unlinked copy of the row below it, with a new Tag ID and alarm IDs. */
      duplicate: (at) => dispatch({ type: "duplicate", at }),
      removeRow: (index) => dispatch({ type: "removeRow", index }),
      removeRows: (indices) => dispatch({ type: "removeRows", indices }),
      /** Replaces one row, marking it dirty only if it differs from its saved baseline. */
      updateRow: (index, row) => dispatch({ type: "updateRow", index, row }),
      /** Replaces the rows, re-checking `indices` against their saved baselines. */
      editRows: (editedTags, indices) =>
        dispatch({ type: "editRows", editedTags, indices }),
      /** Replaces the rows and the dirty set as given. */
      applyEdits: (editedTags, dirtyTags) =>
        dispatch({ type: "applyEdits", editedTags, dirtyTags }),
      /**
       * Makes each saved row its grid row's baseline. `savedRows` (from `getSavedRows`) maps each
       * saved row to its baseline when the save started. A row still unchanged is found as itself,
       * and a row edited during the save by that baseline, which edits leave in place; the edit
       * stays, dirty if it differs from what was saved. Matching by object rather than index
       * follows rows that moved because rows were added or removed during the save.
       */
      reconcileSave: (savedRows) =>
        dispatch({ type: "reconcileSave", savedRows }),
      /** Discards unsaved edits. */
      reset: () => dispatch({ type: "reset" }),
      clear: () => dispatch({ type: "clear" }),
      /** Like a useState setter: takes a value, or an updater that receives a copy. */
      setEditedTags: (value) => dispatch({ type: "setEditedTags", value }),
      setDirtyTags: (value) => dispatch({ type: "setDirtyTags", value }),
      setSelectedTags: (value) => dispatch({ type: "setSelectedTags", value }),
    }),
    []
  );

  const selectedTagRecords = grid.editedTags.filter((_, index) =>
    grid.selectedTags.has(index)
  );

  return { ...grid, ...actions, selectedTagRecords };
};
